import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import Loader from 'src/components/loader';
import Alert from 'src/components/ui/Alert';
import Radio from 'src/components/ui/radio';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { getShortUuid } from 'src/utils/url';

const INITIAL_VISIBLE_COUNT = 3;

const NcrModal = ({
  onConfirm,
  onDismiss,
  selectedPieces,
}) => {
  const [ncrReason, setNcrReason] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showAllPieces, setShowAllPieces] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSplitSchedule, setIsSplitSchedule] = useState(false);

  const { closeActionPanel } = useActionPanelStore();

  const isNcrReasonValid = ncrReason.trim().length > 0;
  const showNcrError = submitAttempted && !isNcrReasonValid;

  const visiblePieces = useMemo(() => {
    if (showAllPieces) {
      return selectedPieces;
    }

    return selectedPieces.slice(0, INITIAL_VISIBLE_COUNT);
  }, [selectedPieces, showAllPieces]);

  const remainingCount =
    selectedPieces.length - INITIAL_VISIBLE_COUNT;

  return (
    <div>
      <div className="mb-3 d-flex flex-wrap gap-5 align-items-center">
        {visiblePieces.map((piece) => (
          <span
            key={piece}
            className="status-badge status-badge--info"
          >
            {getShortUuid(piece)}
          </span>
        ))}

        {remainingCount > 0 && (
          <button
            type="button"
            className="status-badge border-0 bg-transparent status-badge--info text-capitalize"
            onClick={() => setShowAllPieces((prev) => !prev)}
          >
            {showAllPieces
              ? 'Show less'
              : `+ ${remainingCount} more`}
          </button>
        )}
      </div>
      <Alert
        variant="info"
        name="remanufacture-info"
        icon={faInfoCircle}
        iconClassName="mt-1"
        className="d-flex align-items-start gap-10"
      >
        This action automatically schedules a Non-conformance Review as the next step in the workflow for the selected piece(s).
        The Non-conformance Review board will evaluate the reported non-conformance(s) and decide how each piece should proceed.
      </Alert>

      <div className="mb-4">
        <label>
          Reason
          <span className="required-text fw-bold">*</span>
        </label>

        <textarea
          className="form-control"
          value={ncrReason}
          onChange={(event) => setNcrReason(event.target.value)}
        />

        {showNcrError && (
          <span className="required-text small">
            A reason is required before confirming non-conformance review.
          </span>
        )}
      </div>
      <div className="d-flex flex-column gap-10 mb-4">
        <p className='mb-0'>
          Should the other Pieces in the Run wait for the Selected Pieces to complete their new workflow?
          If not, split them into their own Runs to and skip the wait.
        </p>
        <div className="d-flex flex-wrap gap-10">
          <Radio 
            id="keep-schedule"
            name="schedule-option"
            checked={!isSplitSchedule}
            label="Keep the schedule"
            onChange={() => setIsSplitSchedule(false)}
          />
          <Radio 
            id="split-schedule"
            name="schedule-option"
            checked={isSplitSchedule}
            label="Split the schedule"
            onChange={() => setIsSplitSchedule(true)}
          />
        </div>
      </div>
      <div className="button-group button-group--panel">
        <button
          type="button"
          className="button"
          disabled={isSubmitting}
          onClick={() => {
            onDismiss();
            closeActionPanel();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={isSubmitting}
          onClick={async () => {
            setSubmitAttempted(true);
            if (!ncrReason.trim()) return;

            try {
              setIsSubmitting(true);
              await onConfirm({
                notes: ncrReason,
                splitSchedules: isSplitSchedule,
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {!isSubmitting ? 'Confirm NCR' : <Loader inline />}
        </button>
      </div>
    </div>
  );
};

NcrModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  selectedPieces: PropTypes.array.isRequired,
};

export default NcrModal;
